import { submitMailingListSignup } from "../marketing/mailing-list";
/**
 * MarketingLayout — chrome wrapper for all public/marketing routes.
 *
 * Renders the MarketingNav + MarketingFooter and an <Outlet /> for the
 * page content. Sits *outside* the AuthProvider so these routes never
 * trigger Cognito calls.
 *
 * The nav's links and the footer's copy are both CMS content, resolved by the
 * layout route's loader and read here. `PRIMARY_LINKS` and the shipped site
 * settings are the fallbacks, so an unreadable menu costs a stale nav rather
 * than an empty bar, and unreadable settings cost the shipped wording rather
 * than a blank footer.
 *
 * Uses a small `RouterLink` adapter so the nav and footer use react-router
 * navigation (no page reloads) while the underlying components remain
 * router-agnostic.
 */
import { findPageDisclaimer } from "../marketing/page-disclaimer";
import { useEffect } from "react";
import { NavLink, Outlet, useLocation, useRouteLoaderData } from "react-router";
import {
  MarketingNav,
  MarketingFooter,
  ProductDisclaimer,
  SkipLink,
} from "@diality/ui";
import type { MarketingFooterLinkGroup } from "@diality/ui";
import { MarketingBlockProvider } from "../marketing/block-render";
import { LanguageSwitcher } from "../marketing/LanguageSwitcher";
import { type SiteSettings, type TemplateChrome } from "@diality/contracts";
import type { MarketingNavLink } from "../marketing/nav-loader";
import {
  DEFAULT_PAGE_TEMPLATE,
  type PageRibbon,
} from "../marketing/page-loader";
import { organizationStructuredData } from "../marketing/seo";
import { AnalyticsPageViews } from "../analytics/AnalyticsPageViews";
import { ChapterScrollEffects } from "../animation/ChapterScrollEffects";
import { RibbonScene } from "../animation/RibbonScene";
import { SmoothScroll } from "../animation/SmoothScroll";
import { MarketingTransition } from "../animation/MarketingTransition";
import { PRIMARY_LINKS, FOOTER_LINK_GROUPS } from "../marketing/site-config";

/** What the layout route's loader resolves per request. */
export interface MarketingLayoutData {
  analyticsConsent: boolean | null;
  /** The CMS-configured primary nav, or null when the site should use its own. */
  primaryNav: MarketingNavLink[] | null;
  /**
   * The header's call to action, named explicitly on the Main Nav menu.
   *
   * Null when none is named, and the distinction matters: `MarketingNav`
   * supplies its own Contact pill when `cta` is undefined, which is what the
   * site falls back to. Passing null would omit the pill entirely.
   */
  cta?: MarketingNavLink | null;
  /**
   * The footer's three columns as the CMS arranged them, or null when no link
   * is configured at all — which is when the site uses its own groups.
   */
  footerGroups?: MarketingFooterLinkGroup[] | null;
  /**
   * Which pieces of site chrome the page being rendered asks for, from its
   * template. Absent when the layout renders outside its route — the CMS
   * canvas mounts it directly — where both show, as they always did.
   */
  chrome?: TemplateChrome;
  /**
   * The background ribbon the page shows, resolved from its template's offer,
   * its own switch and its blocks' waypoints. Null (or absent) draws none.
   */
  ribbon?: PageRibbon | null;
  /** Stable identity of the published CMS page currently being rendered. */
  currentPageId?: string | null;
  /**
   * The CMS-held site settings — the footer's authored copy, postal address and
   * social profiles. Null when they can't be read; absent when the layout
   * renders outside its route. Both answer with the shipped defaults, which is
   * what the footer already does for an undefined prop.
   */
  settings?: SiteSettings | null;
  /**
   * The year the template-owned copyright line shows, resolved in the loader
   * rather than in the component so SSR and hydration cannot disagree: the
   * client's first render reads this serialized value, not its own clock.
   */
  copyrightYear?: number;
}

/**
 * An href react-router cannot route: another origin, another scheme, or a
 * fragment on the current page. CMS `url` targets can be any of those.
 */
const UNROUTABLE_HREF = /^([a-z][a-z0-9+.-]*:|\/\/|#)/i;

/** Adapter — bridges react-router's `Link` to plain `<a href={href}>` API. */
function RouterLink({
  href,
  children,
  ...rest
}: { href?: string; children?: React.ReactNode } & Record<string, unknown>) {
  const to = href ?? "/";
  // A NavLink to an off-site address would mangle it into an in-app path. Every
  // in-app path must keep taking the NavLink branch below: that is what makes a
  // nav click a client-side navigation instead of a full page load.
  if (UNROUTABLE_HREF.test(to)) {
    return (
      <a href={to} {...(rest as object)}>
        {children}
      </a>
    );
  }
  // Use NavLink so aria-current works automatically; stripped here because
  // MarketingNav passes its own active prop.
  return (
    <NavLink to={to} {...(rest as object)}>
      {children}
    </NavLink>
  );
}

export function MarketingLayout() {
  const { pathname } = useLocation();
  // Resolved server-side by the layout route's loader (undefined when the
  // layout renders outside that route — e.g. the CMS canvas — where the consent
  // banner falls back to reading the cookie after mount and the nav falls back
  // to the site's own links).
  const layoutData =
    useRouteLoaderData<MarketingLayoutData>("layouts/marketing");
  const analyticsConsent = layoutData?.analyticsConsent;
  // Absent on the CMS-canvas mount, null when the settings service is
  // unreadable. Every read below is optional for that reason: an undefined prop
  // reaching the footer is exactly "use the shipped default".
  const settings = layoutData?.settings ?? null;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  const configured = layoutData?.primaryNav;
  const source = configured ?? PRIMARY_LINKS;
  // The call to action is a NAMED FIELD on the menu now, not whichever link
  // happened to point at the contact address. That convention was invisible to
  // an author — nothing on the CMS screen said which link was the button, and
  // reordering could silently change it.
  //
  // `undefined` rather than null when none is named: MarketingNav supplies its
  // own Contact pill for undefined, and that default is what several pages
  // still rely on. Null would omit the pill.
  const cta = layoutData?.cta ?? undefined;

  // Mark the active link based on the current route.
  const links = source
    .filter((link) => link.href !== cta?.href)
    .map((l) => ({
      ...l,
      active:
        l.href === "/"
          ? pathname === "/"
          : pathname === l.href || pathname.startsWith(l.href + "/"),
    }));

  const chrome = layoutData?.chrome ?? DEFAULT_PAGE_TEMPLATE.chrome;
  const ribbon = layoutData?.ribbon ?? null;
  const productDisclaimer = findPageDisclaimer(
    settings?.productDisclaimers ?? [],
    layoutData?.currentPageId,
  );

  return (
    <MarketingBlockProvider>
      {/* `relative isolate`: the ribbon canvas is fixed at z-index -1 and this
          root's own background paints beneath it inside the isolated context —
          the prototype's `#root { isolation: isolate }`. When a ribbon is
          present, the dedicated root class is made transparent; child surfaces
          such as consent panels retain their own backgrounds. */}
      <div
        className="marketing-body-content relative isolate flex min-h-dvh flex-col bg-background text-foreground"
        data-ribbon={ribbon?.opening ?? undefined}
      >
        <MarketingTransition>
          <RibbonScene ribbon={ribbon} pathname={pathname} />
          <script type="application/ld+json">
            {JSON.stringify(organizationStructuredData())}
          </script>
          <SmoothScroll />
          <ChapterScrollEffects pathname={pathname} />
          <SkipLink href="#main">Skip to main content</SkipLink>
          {chrome.header ? (
            <div>
              <MarketingNav
                data-site-header
                brandHref="/"
                links={links}
                cta={cta}
                socialLinks={settings?.socialLinks}
                linkAs={RouterLink}
              />
              {/* The switcher stays mounted and reachable while its visual bar is
                hidden. Keeping it inside the conditional header also prevents
                hidden-header templates from retaining stray header chrome. */}
              <div>
                <div className="sr-only">
                  <LanguageSwitcher />
                </div>
              </div>
            </div>
          ) : null}
          {/* The fixed nav occupies 84px: 94px with a -10px offset on desktop,
              and 84px at top: 0 on mobile (matching InlineNavFrame in the CMS). */}
          <main
            id="main"
            tabIndex={-1}
            className={
              chrome.header
                ? "@container flex-1 pt-[84px]"
                : "@container flex-1"
            }
          >
            <Outlet />
          </main>
          {chrome.footer ? (
            <div data-site-footer>
              <MarketingFooter
                submitMailingListSignup={submitMailingListSignup}
                linkGroups={layoutData?.footerGroups ?? FOOTER_LINK_GROUPS}
                newsletterHeading={settings?.footer?.newsletterHeading}
                newsletterPlaceholder={settings?.footer?.newsletterPlaceholder}
                newsletterSubmitLabel={settings?.footer?.newsletterSubmitLabel}
                address={settings?.address}
                socialLinks={settings?.socialLinks}
                siteName={settings?.siteName}
                copyrightYear={layoutData?.copyrightYear}
                copyrightNotice={settings?.footer?.copyrightNotice}
                linkAs={RouterLink}
              />
            </div>
          ) : null}
          <div
            className="pointer-events-none fixed inset-x-4 bottom-4 z-50 flex flex-col items-center gap-4 min-[551px]:flex-row min-[551px]:items-end min-[551px]:justify-between"
            data-fixed-overlays
          >
            <AnalyticsPageViews
              initialConsent={analyticsConsent}
              settings={settings}
            />
            {productDisclaimer ? (
              <ProductDisclaimer
                key={productDisclaimer.pageId}
                body={productDisclaimer.body}
                className="pointer-events-auto min-w-[196px] shrink"
              />
            ) : null}
          </div>
        </MarketingTransition>
      </div>
    </MarketingBlockProvider>
  );
}
